import { WebSocketSubject, webSocket } from "rxjs/webSocket";
import { Subscription, filter, Observable, PartialObserver, tap, ReplaySubject } from "rxjs";

const log = (...data: any[]) => {
  if (import.meta.env.DEV) {
    console.log("[PubSubSubject]", ...data);
  }
};

export const logEvent = tap((val) => log("received", val)) as <T>(source: Observable<T>) => Observable<T>;
export const filterNull = filter((val) => val !== null) as <T>(source: Observable<T | null>) => Observable<T>;
export const filterAction = (action: string) => filter((val: any) => val?.action === action) as <T>(source: Observable<T>) => Observable<T>;

/**
 * An Rxjs subject that wraps a websocket and emits events when the socket receives an event.
 * The socket only only opens if there is at least 1 subscriber.
 * Should be able to subscribe regardless of the socket connection state.
 */
export class PubSubSubject extends ReplaySubject<any> {
  private socket: WebSocketSubject<any> | null;
  private url: string | (() => Promise<string>);
  private connecting = false;
  private numObservers: number;

  constructor(url: string | (() => Promise<string>)) {
    super(1);
    this.numObservers = 0;
    this.url = url;
    this.socket = null;
  }

  // gets the url, creates the socket connection and subscribes to the socket
  private async connect() {
    if (!this.socket && !this.connecting) {
      try {
        this.connecting = true;
        log("connecting...");
        let source = null;
        if (typeof this.url === "string") {
          source = this.url;
        } else {
          source = await this.url();
        }
        this.socket = webSocket(source);
        log("connected", source);
        // bind internal socket events to this subject
        // do not bind complete, as we want to keep this subject alive
        this.socket
          .pipe(logEvent)
          .subscribe({
            // doesn't like just passing this.next
            next: (val) => this.next(val),
            error: (err) => this.error(err),
          });
      } catch (err) {
        console.error("[PubSubSubject] failed to connect", err);
      } finally {
        this.connecting = false;
      }
    }
  }

  // @ts-ignore The type is conflicting with a deprecated overload
  subscribe<T>(observerOrNext?: PartialObserver<T> | ((value: T) => void)): Subscription {
    log("new subscriber");
    const sub = super.subscribe(observerOrNext);
    this.numObservers++;

    if (!this.socket) {
      this.connect();
    }

    sub.add(() => {
      this.numObservers--;
      log("subs left", this.numObservers);
      if (this.numObservers === 0 && this.socket) {
        log("clearing socket");
        this.socket.unsubscribe();
        this.socket = null;
      }
    });

    return sub;
  }

}